/**
 * client/src/cma/draftStore.ts — where an in-progress review is parked, and how long it lives.
 *
 * WHY THIS IS ITS OWN MODULE (2026-09-07, owner: "we can no longer go back to any stage or step in
 * the CMA and AMB process ... we used to be able to").
 *
 * Nothing in the navigation code had changed. `CmaAmbFlow.canGoToStep` opens the three reviewing
 * nodes only when `hasReview` — a job (or a resolved cascade) AND a non-empty grid — because a
 * breadcrumb pointing at a screen with nothing on it is a lie. What changed is how a finished brief
 * gets opened: from the Briefs list, in a tab that never ran the wizard. The draft lived only in
 * `sessionStorage`, which the browser drops when the tab closes, so on that tab the grid rehydrated
 * EMPTY, `hasReview` was false, and every reviewing node rendered as an inert `<span>` beside a
 * step 1 that is never a door from the report. Five nodes, no doors — exactly what the owner sent
 * screenshots of.
 *
 * The fix is not to loosen the gate. `hasReview` is right: the rail should not offer a step with
 * nothing on it. The fix is that the agent's own reviewed grid should still BE there.
 *
 * SO: THE SAME DRAFT, WRITTEN TWICE.
 *
 *   · `sessionStorage` keeps the behaviour every existing test pins and stays the FIRST read. It is
 *     the tab's own copy, so two tabs reviewing two properties cannot read each other's work.
 *   · `localStorage` is the copy that survives the tab, read only when the tab has none. Keyed by
 *     the job id, so the copy that comes back is the one written for THIS brief.
 *   · `amb_jobs.review_json` is the copy that survives the MACHINE, read only when neither local
 *     store has one. Same key, same body, and the server never looks inside it.
 *
 * WHAT IS IN IT, AND THEREFORE WHAT IS BEING KEPT LONGER. Property facts the agent is already
 * looking at, their own value adjustments, and — since 2026-08-05 — `preparedFor`, a homeowner's
 * name. That is the whole reason this module has an expiry and a sweep rather than just a second
 * `setItem`:
 *
 *   · `savedAt` on every durable copy, and `MAX_AGE_MS` (30 days) enforced ON READ and on every
 *     sweep. A draft older than that is not returned and is deleted the moment it is looked at.
 *   · `clearAllDrafts()` empties BOTH stores. Start over and signing out both call it.
 *
 * ITS OWN PREFIX, DELIBERATELY NARROWER THAN THE FAMILY'S. `ConfirmGrid` keeps its table/card
 * preference in `localStorage` at `vb:cma:comp-view` — same family, not a draft, not anybody's
 * data. A sweep that matched `vb:cma:` would take it, and a sweep coarse enough to take that is one
 * that will one day take something it was never told about. The durable copies live under
 * `vb:cma:draft:` and the sweep is exact about which prefix belongs to which store.
 *
 * AND SINCE 2026-09-07 THERE IS A THIRD COPY, ON THE SERVER. The paragraph that used to sit here
 * stated the limit — "this is one browser on one machine" — and filed the close as a schema change
 * rather than smuggling it in. That change landed: `amb_jobs.review_json` holds the confirmed
 * review, `saveDraft` mirrors it there, and `loadServerDraft` fetches it when neither local store
 * has one. An agent who reviews on a laptop and opens the brief on a desktop gets doors.
 *
 * THE ORDER IS TAB, THEN MACHINE, THEN SERVER, and it is not arbitrary. The nearest copy is the
 * freshest: the tab holds what the agent is doing right now, the machine holds what they did before
 * closing it, and the server holds what they last confirmed anywhere. Reading the server first
 * would let a save that has not landed yet be overwritten by the one before it.
 *
 * THE SERVER TIER IS ASYNC AND THE OTHER TWO ARE NOT, so it is a SEPARATE function rather than an
 * awaited branch inside `loadDraft`. Every existing caller reads a draft synchronously while
 * rendering; making that async would have turned one storage question into a loading state on
 * every screen that asks it.
 *
 * WHAT IS STILL NOT DONE, stated rather than papered over: reconstructing a grid from the finished
 * report's payload. `data.your_home` and `data.cards` are display strings, and rebuilding inputs
 * from them would invent the one thing this product sells — the source tag on every row. A brief
 * whose review was never confirmed on any machine has no grid to come back to, and the rail is
 * right to show no doors.
 */

import { getJobReview, putJobReview } from "./api";

/** The tab's own copy — unchanged, and still the first thing read. */
const SESSION_PREFIX = "vb:cma:";
/** The copy that outlives the tab. Narrower on purpose — see the module note. */
const LOCAL_PREFIX = "vb:cma:draft:";

const sessionKey = (jobId: string) => `${SESSION_PREFIX}${jobId}`;
const localKey = (jobId: string) => `${LOCAL_PREFIX}${jobId}`;

/** How long a durable draft is allowed to sit on the machine. Thirty days is long enough that a
 * brief picked back up after a holiday still has its review, and short enough that a name does not
 * live on a shared workstation indefinitely. */
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/** The one field this module adds. Everything else in a draft belongs to the caller and is written
 * back untouched — this module never inspects, reshapes or drops any of it. */
interface Stamped {
  vbSavedAt?: number;
}

/** Storage is absent in private mode, refused at quota, and simply not there on some jsdom origins.
 * Every access here goes through this, so a browser that says no costs the agent a durable draft
 * and nothing else. */
function store(which: "session" | "local"): Storage | null {
  try {
    const s = which === "session" ? window.sessionStorage : window.localStorage;
    return s ?? null;
  } catch {
    return null;
  }
}

/** Keys currently in a store, snapshotted BEFORE anything is removed: removing while iterating
 * renumbers the remaining keys under the cursor and silently skips one. */
function keysWithPrefix(s: Storage, prefix: string): string[] {
  const out: string[] = [];
  for (let i = 0; i < s.length; i++) {
    const k = s.key(i);
    if (k && k.startsWith(prefix)) out.push(k);
  }
  return out;
}

function fresh(raw: string | null): boolean {
  if (raw === null) return false;
  try {
    const at = (JSON.parse(raw) as Stamped).vbSavedAt;
    // A durable copy written before the stamp existed has no age we can defend, so it is treated as
    // expired rather than kept forever on a guess.
    return typeof at === "number" && Date.now() - at < MAX_AGE_MS;
  } catch {
    return false;
  }
}

/**
 * Park the draft in both stores. The session copy is written first and is what every existing
 * behaviour depends on; a failure to write the durable copy is not allowed to cost the agent the
 * session one.
 */
/**
 * HELD MLS ROWS ARE MEMORY ONLY (comps-ux fix round). The rows an MLS pull set aside (`_held_mls`)
 * can be brought on screen by a filter change and so land in `comps`; they are removed here, from
 * every array in the draft, before ANY copy (session, durable, server) is written. NTREIS storage
 * terms for them are unconfirmed. A held row the agent ticked is therefore not restored by a reload.
 */
/** In-memory-only mark on a row that came from a held MLS row after `_held_mls` was removed for
 * the wire (the confirmed payload). A Symbol key: JSON.stringify skips it, so it never reaches the
 * server, and object spread / rest copies it, so it survives a clone. */
export const HELD_MLS_MARK: unique symbol = Symbol.for("vb.heldMls") as never;

function isHeldRow(r: unknown): boolean {
  if (!r || typeof r !== "object") return false;
  const o = r as Record<PropertyKey, unknown>;
  return o._held_mls === true || o[HELD_MLS_MARK] === true;
}

/** Mark a wire row (already stripped of `_held_mls`) as held, memory only. */
export function markHeld<T extends object>(row: T): T {
  Object.defineProperty(row, HELD_MLS_MARK, { value: true, enumerable: true, configurable: true });
  return row;
}

function withoutHeldIn(arr: unknown): unknown {
  return Array.isArray(arr) && arr.some(isHeldRow) ? arr.filter((r) => !isHeldRow(r)) : arr;
}

export function withoutHeldRows<T extends object>(draft: T): T {
  const out: Record<string, unknown> = { ...(draft as Record<string, unknown>) };
  for (const [k, v] of Object.entries(out)) out[k] = withoutHeldIn(v);
  // The confirmed payload nests its rows (`comps`, `meta.actives`) one level down, with the
  // `_held_mls` flag already removed for the wire; the memory-only mark finds them there. Removing
  // rows from it would leave `meta._overrides.comps[].index`, `comp_primacy` and `actives_now`
  // pointing at the wrong rows, so a payload that held any is NOT stored at all: after a reload
  // there is no confirmed build to re-voice and the agent rebuilds, exactly as for a draft saved
  // before `confirmedPayload` existed.
  const cp = out.confirmedPayload as { comps?: unknown; meta?: Record<string, unknown> } | null | undefined;
  if (cp && typeof cp === "object") {
    const heldComps = Array.isArray(cp.comps) && cp.comps.some(isHeldRow);
    const heldActives = Array.isArray(cp.meta?.actives) && (cp.meta?.actives as unknown[]).some(isHeldRow);
    if (heldComps || heldActives) {
      out.confirmedPayload = null;
      // Data-free: says only THAT a payload was withheld, so the screen can say why re-voice is
      // unavailable after a reload instead of dropping the control without a word.
      out.confirmedPayloadWithheld = true;
    }
  }
  return out as T;
}

export function saveDraft<T extends object>(jobId: string, rawDraft: T): void {
  const draft = withoutHeldRows(rawDraft);
  const body = JSON.stringify(draft);
  try {
    store("session")?.setItem(sessionKey(jobId), body);
  } catch {
    /* Private mode / quota — the flow still works, it just won't survive a refresh. Never fatal. */
  }
  try {
    store("local")?.setItem(localKey(jobId), JSON.stringify({ ...draft, vbSavedAt: Date.now() }));
  } catch {
    /* Same posture. Losing the durable copy costs back-navigation from a new tab, nothing more. */
  }
  mirrorToServer(jobId, body, draft);
}

/**
 * The server copy, written behind the two local ones and never in front of them.
 *
 * COALESCED, BECAUSE THE CALLER SAVES ON EVERY KEYSTROKE. `CmaAmbFlow` writes the draft from an
 * effect over sixteen pieces of state, so typing an address is a save per character. The local
 * stores absorb that; a request per character would not. One in-flight write per job, and the last
 * body queued while it is in flight is sent after it — the LAST one, not each of them, because the
 * only thing anybody wants back is the newest.
 *
 * IDENTICAL BODIES ARE NOT RE-SENT. The effect fires on state that did not change the draft, and a
 * PUT that stores what is already stored is a request that costs an agent's connection nothing but
 * latency.
 *
 * IT IS NEVER FATAL AND NEVER VISIBLE. A failed mirror costs back-navigation on a SECOND machine
 * and nothing on this one, so it must not surface an error over a review the agent can still see.
 * The one thing it does not do is retry: a review that could not be stored will be stored by the
 * next edit, and a retry loop against a 413 would be a loop.
 */
const inFlight = new Map<string, string>();
const queued = new Map<string, string>();
const lastSent = new Map<string, string>();

function mirrorToServer<T extends object>(jobId: string, body: string, draft: T): void {
  if (lastSent.get(jobId) === body) return;
  if (inFlight.has(jobId)) {
    queued.set(jobId, body);
    return;
  }
  inFlight.set(jobId, body);
  void putJobReview(jobId, draft)
    .then(() => { lastSent.set(jobId, body); })
    .catch(() => { /* see the note above: costs a second machine, never this one */ })
    .finally(() => {
      inFlight.delete(jobId);
      const next = queued.get(jobId);
      if (next !== undefined && next !== body) {
        queued.delete(jobId);
        try {
          mirrorToServer(jobId, next, JSON.parse(next) as T);
        } catch {
          /* an unparseable queued body is a bug we will not compound by throwing in a finally */
        }
      } else {
        queued.delete(jobId);
      }
    });
}

/**
 * The server's copy of this brief's confirmed review, or `null`.
 *
 * ASKED ONLY WHEN BOTH LOCAL STORES CAME BACK EMPTY — see the order in the module note. Returns
 * `null` on any failure, including a 404: from here, "the server has no review for this brief" and
 * "the server would not tell me" lead to the same screen, and inventing a difference the caller
 * cannot act on would only add a branch nobody can test.
 */
export async function loadServerDraft<T>(jobId: string): Promise<T | null> {
  try {
    return await getJobReview<T>(jobId);
  } catch {
    return null;
  }
}

/**
 * The tab's copy if it has one; otherwise the durable copy, if it is still young enough.
 *
 * The session copy is NOT age-checked: it cannot outlive the tab, and a tab open for thirty-one
 * days is a tab whose work is still on screen.
 */
export function loadDraft<T>(jobId: string): T | null {
  try {
    const mine = store("session")?.getItem(sessionKey(jobId));
    if (mine) return JSON.parse(mine) as T;
  } catch {
    /* fall through to the durable copy */
  }
  const local = store("local");
  if (!local) return null;
  try {
    const raw = local.getItem(localKey(jobId));
    if (!fresh(raw)) {
      // Expired or unreadable, and now gone: a draft we will not hand back is a draft that has no
      // business staying on the machine.
      if (raw !== null) local.removeItem(localKey(jobId));
      return null;
    }
    const { vbSavedAt: _at, ...draft } = JSON.parse(raw as string) as Stamped & Record<string, unknown>;
    return draft as T;
  } catch {
    return null;
  }
}

/**
 * EVERY DRAFT THIS FLOW HAS WRITTEN, GONE — from both stores.
 *
 * Not `clearDraft(job.id)`: one pass through the wizard writes the draft under TWO ids, the
 * extract job's while the agent reviews and the build job's when the submit copies it across so a
 * failed build still has a review to go back to. And not `clear()` on either store — this flow does
 * not own the whole of `sessionStorage` and owns considerably less of `localStorage`.
 */
export function clearAllDrafts(): void {
  const session = store("session");
  if (session) {
    try {
      for (const k of keysWithPrefix(session, SESSION_PREFIX)) session.removeItem(k);
    } catch {
      /* non-fatal */
    }
  }
  const local = store("local");
  if (local) {
    try {
      for (const k of keysWithPrefix(local, LOCAL_PREFIX)) local.removeItem(k);
    } catch {
      /* non-fatal */
    }
  }
}

/**
 * Drop every durable draft that has aged out, wherever the app happens to start.
 *
 * `loadDraft` already expires the one draft it is asked for, but a draft nobody ever reopens is
 * never asked for — and that is precisely the one that should not still be here. Called once on
 * mount so the thirty days is a real ceiling rather than a promise kept only for briefs that get
 * revisited.
 */
export function sweepExpiredDrafts(): void {
  const local = store("local");
  if (!local) return;
  try {
    for (const k of keysWithPrefix(local, LOCAL_PREFIX)) {
      if (!fresh(local.getItem(k))) local.removeItem(k);
    }
  } catch {
    /* non-fatal */
  }
}
